import React, { Suspense, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Router, Route, Switch } from "react-router-dom";
import { DesignCore } from "ui-components";
import history from "utils/history";
import {
  routes,
  publicRoutes,
  authorizedRoutes,
  eligibilityRoutes
} from "../../routes";
import ReactModal from "react-modal";
import map from "lodash/map";
import DataLoader from "containers/DataLoader";
import Alert from "components/Alert";
import Helmet from "components/Helmet";
import AuthorizedRoute from "components/AuthorizedRoute";
import PublicRoute from "components/PublicRoute";
import EligibilityRoute from "components/EligibilityRoute";
import Layout from "components/Layout";
import Spinner, { styles } from "components/Spinner";
import { gotoUrl, handleRouteLoading } from "utils/helpers";
import AnalyticsFactory from "analytics/AnalyticsFactory";
import { initPushNotification } from "analytics";
import { resetLoadingApis } from "ducks/actions";
import PageError from "containers/PageError";
import TermsConditions from "../TermsConditions";
import PrivacyPolicy from "../PrivacyPolicy";
import { paths } from "utils/constants";
import LaunchDarklyProvider from "providers/LaunchDarklyProvider";

const { ThemeProvider, theme, Global, globalStyles } = DesignCore;
ReactModal.setAppElement("#root");

function App(props) {
  const [routeLoading, setRouteLoading] = useState(true);
  const dispatch = useDispatch();

  // For Dev testing
  // fetchRequiredDataHelper();

  useEffect(() => {
    dispatch(resetLoadingApis());
    initPushNotification(props.store);
    AnalyticsFactory.pushEvent("page_visit", {}, "", props.store);
    handleRouteLoading(setRouteLoading);
    return history.listen(() => {
      AnalyticsFactory.pushEvent("page_visit", {}, "", props.store);
      handleRouteLoading(setRouteLoading);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Renders the route rpovided component encapsulated with few HOCs
   * @param {object} componentProps
   * @param {ReactNode} ComponentToRender
   */
  const renderComponent = (componentProps, ComponentToRender) => {
    window.scrollTo(0, 0);
    return (
      <DataLoader
        {...componentProps}
        render={({ loading, requiresMaintenance }) => {
          if (requiresMaintenance) {
            gotoUrl(paths.MAINTENANCE);
          } else if (loading || routeLoading) {
            return <Spinner size={200} sx={styles.fullPage} />;
          }

          return (
            <>
              <Helmet />
              <ComponentToRender {...componentProps} />
            </>
          );
        }}
      />
    );
  };
  const launchDarklyOptions = {
    enabled: true
    //see https://launchdarkly.github.io/js-client-sdk/interfaces/LDOptions.html for details of other options
  };

  return (
    <ThemeProvider theme={theme}>
      <Global styles={globalStyles} />

      <Router history={history}>
        <Suspense fallback={<Spinner size={200} sx={styles.fullPage} />}>
          <LaunchDarklyProvider options={launchDarklyOptions}>
            <Switch>
              <Route
                path="/terms-conditions/general"
                exact
                render={props => renderComponent(props, TermsConditions)}
              />
              <Route
                path="/terms-conditions/privacy-policy"
                exact
                render={props => renderComponent(props, PrivacyPolicy)}
              />
              {map(routes, ({ path, Component }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <Route
                    key={path}
                    path={path}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}

              {map(
                authorizedRoutes,
                ({
                  path,
                  Component,
                  auth,
                  orderAutorized = false,
                  activeSIMOrder = false
                }) => {
                  const ComponentToRender = Layout(Component);
                  return (
                    <AuthorizedRoute
                      key={path}
                      path={path}
                      auth={auth}
                      orderAutorized={orderAutorized}
                      activeSIMOrder={activeSIMOrder}
                      exact
                      render={props =>
                        renderComponent(props, ComponentToRender)
                      }
                    />
                  );
                }
              )}

              {map(eligibilityRoutes, ({ path, Component }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <EligibilityRoute
                    key={path}
                    path={path}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}

              {map(publicRoutes, ({ path, Component }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <PublicRoute
                    key={path}
                    path={path}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}

              <Route path="/:unknown" exact component={PageError} />
              <Route component={PageError} />
            </Switch>
          </LaunchDarklyProvider>
        </Suspense>
      </Router>

      <Alert stack={{ limit: 1 }} html position="top" effect="stackslide" />
    </ThemeProvider>
  );
}

export default App;
